import React, { useState } from "react";
import { useSelector } from "react-redux";
import { gradesList } from "../../../enums/gradesEnum";
import { targetGroupTextType } from "../../../enums/targetGroupType";
import TreeSelectField from "../treeSelectField/TreeSelectField";
import TreeSelectRow from "../treeSelectField/treeSelectRow/TreeSelectRow";

const AudienceField = ({ value, selectedGrades = [], onChange, onGradesChange }) => {
    const { texts } = useSelector((state) => state.translate);
    const defaultSchool = useSelector((state) => state.auth.userDetails?.defaultSchool);
    const [sections, setSections] = useState({ dropdown: false, grades: false });
    const toggleSection = (key) => setSections(prev => ({ ...prev, [key]: !prev[key] }));

    const schoolGrades = gradesList.filter(g =>
        g.value >= (defaultSchool?.fromClass ?? 1) &&
        g.value <= (defaultSchool?.toClass ?? 12)
    );

    const isTeacher = (v) => v === targetGroupTextType.Teacher || v === targetGroupTextType.Everyone;
    const isStudent = (v) => v === targetGroupTextType.Student || v === targetGroupTextType.Everyone;

    const isGradeInList = (grade) => selectedGrades.some(g => g.value === grade.value);
    const allGradesSelected = selectedGrades.length === schoolGrades.length;

    const toggleGradeInList = (grade) =>
        isGradeInList(grade)
            ? selectedGrades.filter(g => g.value !== grade.value)
            : [...selectedGrades, grade];

    const flipAudienceType = (currentValue, flippedType) => {
        const teacher = flippedType === targetGroupTextType.Teacher ? !isTeacher(currentValue) : isTeacher(currentValue);
        const student = flippedType === targetGroupTextType.Student ? !isStudent(currentValue) : isStudent(currentValue);
        if (teacher && student) return targetGroupTextType.Everyone;
        if (teacher) return targetGroupTextType.Teacher;
        if (student) return targetGroupTextType.Student;
        return null;
    };

    const handleAudienceChange = (action, grade) => {
        switch (action) {
            case targetGroupTextType.Teacher: {
                onChange(flipAudienceType(value, targetGroupTextType.Teacher));
                break;
            }
            case targetGroupTextType.Student: {
                if (allGradesSelected) {
                    onChange(flipAudienceType(value, targetGroupTextType.Student));
                    onGradesChange([]);
                } else {
                    onChange(isTeacher(value) ? targetGroupTextType.Everyone : targetGroupTextType.Student);
                    onGradesChange(schoolGrades);
                }
                break;
            }
            default: {
                const updatedGrades = toggleGradeInList(grade);
                onGradesChange(updatedGrades);
                if (updatedGrades.length === 0) {
                    onChange(isTeacher(value) ? targetGroupTextType.Teacher : null);
                } else if (!isStudent(value)) {
                    onChange(isTeacher(value) ? targetGroupTextType.Everyone : targetGroupTextType.Student);
                }
                break;
            }
        }
    };

    const getChips = () => {
        const chips = [];
        if (isTeacher(value))
            chips.push({ label: texts.toolAudienceTeacher, onClear: () => handleAudienceChange(targetGroupTextType.Teacher) });
        if (isStudent(value) && allGradesSelected)
            chips.push({ label: texts.toolAudienceStudent, onClear: () => handleAudienceChange(targetGroupTextType.Student) });
        if (isStudent(value) && !allGradesSelected)
            selectedGrades.forEach(g => chips.push({ label: g.label, onClear: () => handleAudienceChange(null, g) }));
        return chips;
    };

    return (
        <TreeSelectField
            label={texts.toolAudience}
            placeholder={texts.audiencePlaceholder}
            chips={getChips()}
            open={sections.dropdown}
            onToggle={() => toggleSection("dropdown")}
            onClose={() => setSections(prev => ({ ...prev, dropdown: false }))}
        >
            <TreeSelectRow
                label={texts.toolAudienceTeacher}
                checked={isTeacher(value)}
                onChange={() => handleAudienceChange(targetGroupTextType.Teacher)}
                indent={32}
            />
            <TreeSelectRow
                label={texts.toolAudienceStudent}
                checked={allGradesSelected}
                onChange={() => handleAudienceChange(targetGroupTextType.Student)}
                indent={32}
                expandable
                expanded={sections.grades}
                onExpand={() => toggleSection("grades")}
            />
            {sections.grades && schoolGrades.map(grade => (
                <TreeSelectRow
                    key={grade.value}
                    label={grade.label}
                    checked={isGradeInList(grade)}
                    onChange={() => handleAudienceChange(null, grade)}
                    indent={48}
                />
            ))}
        </TreeSelectField>
    );
};

export default AudienceField;
